import React, { useEffect } from 'react'

import { useReportDownload } from '../../../shared/download-report/hooks/useReportDownload'
import { useAvailableIncidentActions } from '../../../shared/hooks/useAvailableIncidentActions'
import { useGridTesting } from '../../../shared/hooks/useGridTesting'
import { useSendIncidentToResolver } from '../../../shared/hooks/useSendIncidentToResolver'
import { useSendIncidentsToOntic } from '../../../shared/hooks/useSendIncidentsToOntic'
import { useSetIsIncidentReal } from '../../../shared/hooks/useSetIsIncidentReal'
import { IncidentTab, useTabs } from '../../../shared/hooks/useTabs'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { getFloorPlanHref, useNavigation } from '@/hooks/useNavigation'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useWindowSize } from '@react-hook/window-size'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import {
  ActionMenu,
  AssociateIncidentIcon,
  Box,
  DownloadIcon,
  EmergencyCallFilledIcon,
  GridTestingIcon,
  GuardsIcon,
  KebabHorizontalIcon,
  LocationBreadcrumb,
  MapIcon,
  NoteIcon,
  ResolveIcon,
  SOPIcon,
  SendIcon,
  ToolIcon,
  UtilityButton,
  WorkflowButton,
  useDisclosure,
} from '@hivewatch/dark'
import type { ActionMenuItem as ActionMenuItemType } from '@hivewatch/dark'

import { ConfirmEmergencyCallDialog, useCall } from '@/features/emergency-call'
import {
  StandaloneGridTestingModal,
  TailgateGridProvider,
} from '@/features/tailgate-grid'
import { IncidentQueryResult } from '@/graphql/generated/hooks'
import {
  DeviceType,
  FacilityType,
  IncidentStatus,
  IncidentWorkflowStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeIncidentMapPanelDrawer,
  closeSOPIncidentDrawer,
  closeTraceLogDrawer,
  closeWorkflowDrawer,
  openDeviceDrawer,
  selectIncidentMapPanelDrawerIsOpen,
  selectSOPIncidentDrawerState,
  selectWorkflowDrawerIsOpen,
  setDevice,
  toggleIncidentMapPanelDrawer,
  toggleSOPIncidentDrawer,
  toggleWorkflowDrawer,
} from '@/redux/ui/uiSlice'
import {
  GTZ_ENTER,
  SEND_TO_ONTIC_DISABLED_TOOLTIP_SHOWN,
  trackEvent,
} from '@/utils/analytics'
import { isDeviceDrawerSupported } from '@/utils/devices'
import { getArchivedLabel, getArchivedLabelWithSN } from '@/utils/formats'
import { getDefaultOrFirstFloorId } from '@/utils/helpers'

import {
  MAP_PANEL_DRAWER_CLOSE,
  MAP_PANEL_DRAWER_OPEN,
} from '../../../shared/constants/analyticsEvents'
import {
  MAP_PANEL_DRAWER_BREAKPOINT,
  SOP_DRAWER_BREAKPOINT_REBRAND,
} from '../../../shared/constants/drawerDimensions'
import type { WorkflowReportDataI } from '../../../shared/download-report/types/types'
import { SendWebhookModal } from '../../../shared/send-webhook/components/SendWebhookModal'
import { MixpanelDataIProps } from '../../../shared/types/types'
import {
  getOnticDisabledTooltipKey,
  getResolverDisabledTooltipKey,
} from '../../../shared/utils/helpers'
import { getIncidentMapCoordinates } from '../../../shared/utils/mapCoordinates'
import { WORKFLOW_DRAWER_BREAKPOINT } from '../../drawers/workflow/WorkflowDrawer'
import {
  AiOperatorActionIcon,
  AioEvalResultsModals,
  useAioEvalResults,
} from '../../modals/AioEvalResultsModal'
import { AssociateCaseModal } from '../../modals/associate-case/AssociateCaseModal'
import { AnimatedPanel } from './AnimatedPanel'
import { AddNote } from './add-note/AddNote'
import { DispatchWhiteListAlert } from './guard-action/DispatchWhiteListAlert'
import { GuardPanel } from './guard-action/GuardPanel'
import { OrderRepair } from './order-repair/OrderRepair'
import { Resolve } from './resolve/Resolve'
import { ResolveToggle } from './resolve/ResolveToggle'

const ACTION_BUTTON_SIZE = { width: '50px', height: '46px' }
// Compact trigger sized to sit inline with the 12px breadcrumb text rather
// than the incident action buttons.
const MAP_PANEL_BUTTON_SIZE = { width: '28px', height: '28px' }

// -- Main component --

interface ActionBarIProps {
  hasWorkflows: boolean
  incident: IncidentQueryResult['data']['incident']
  isLoading: boolean
  isLoadingWorkflow?: boolean
  isWorkflowFeatureEnabled: boolean
  mixpanelData?: MixpanelDataIProps
  onClose: () => void
  refetchFeedItems?: () => void
  workflowReportData?: WorkflowReportDataI
  workflowStatus: IncidentWorkflowStatus | null
}

export const ActionBar = ({
  hasWorkflows,
  incident,
  isLoading,
  isLoadingWorkflow = false,
  isWorkflowFeatureEnabled,
  mixpanelData,
  onClose,
  refetchFeedItems,
  workflowReportData,
  workflowStatus,
}: ActionBarIProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const colorScheme = useLightMode()
  const sopDrawerState = useAppSelector(selectSOPIncidentDrawerState)
  const isWorkflowDrawerOpen = useAppSelector(selectWorkflowDrawerIsOpen)
  const { goToFloorPlan } = useNavigation()
  const [width] = useWindowSize()
  const isSOPDisabled = width < SOP_DRAWER_BREAKPOINT_REBRAND
  // Incident Drawer Map Panel: placeholder trigger next to the breadcrumb
  // trail (design will restyle later). Rendered only when a point resolves —
  // the incident's own coordinates for Market Facility Incidents, otherwise
  // the facility's — with no empty or disabled state for missing coords.
  const isMapPanelEnabled = useFeatureFlag(FeatureFlag.incidentDrawerMapPanel)
  // Environments on the IPv6 redirection flag can't reach Mapbox, so every map
  // surface is withheld there rather than rendering a broken one (ENG-33659).
  const isIPv6RedirectionEnabled = useFeatureFlag(FeatureFlag.ipv6Redirection)
  const isMapPanelDrawerOpen = useAppSelector(
    selectIncidentMapPanelDrawerIsOpen
  )
  const isMapPanelTooNarrow = width < MAP_PANEL_DRAWER_BREAKPOINT
  // Null for Email Incidents, which have no map (ENG-35813).
  const shouldShowMapPanelButton =
    isMapPanelEnabled &&
    !isIPv6RedirectionEnabled &&
    getIncidentMapCoordinates(incident) != null
  // Landing floor for the breadcrumb's Facility crumb (ENG-32089): the
  // incident's own floor when it has one, otherwise the facility's default (or
  // first) floor so the link still resolves. Undefined when neither exists,
  // which correctly renders no link.
  const facilityFloorId =
    incident?.floor?.id ?? getDefaultOrFirstFloorId(incident?.facility)
  // Legacy suffixes every breadcrumb tooltip with ARCHIVED off the facility's
  // flag — including the device and floor crumbs — so dark matches.
  const isFacilityArchived = incident?.facility?.archived
  const { showError } = useToasts({ isRebrand: true, colorScheme })

  // The dark package has no Sentry, so the breadcrumb hands clipboard failures
  // back here to be reported and surfaced — same toast copy as legacy.
  const handleBreadcrumbCopyError = (error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        action: 'copy',
        feature: 'incident_drawer',
        surface: 'location_breadcrumb',
      },
    })
    showError(t('common:copy_failed'))
  }
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const shouldShowWorkflowButton = isWorkflowFeatureEnabled
  const isWorkflowTooNarrow = width < WORKFLOW_DRAWER_BREAKPOINT
  const isWorkflowDisabled = isWorkflowTooNarrow || !hasWorkflows

  const isResolveBlockedByWorkflow =
    isWorkflowFeatureEnabled &&
    workflowStatus === IncidentWorkflowStatus.InProgress

  const workflowTooltipLabel = isWorkflowTooNarrow
    ? t('incidents:workflow_wider_screen_required')
    : !hasWorkflows
    ? t('incidents:workflow_not_configured')
    : undefined

  const { isInternalImplementer } = usePermissions()
  const { setIncidentReal, isLoading: isUpdatingIncidentReal } =
    useSetIsIncidentReal()
  const aioEval = useAioEvalResults(incident?.id)

  const gridTesting = useGridTesting(incident)

  const {
    open: isAssociateCaseModalOpen,
    onOpen: onAssociateCaseModalOpen,
    onClose: onAssociateCaseModalClose,
  } = useDisclosure()

  const {
    open: isEmergencyCallDialogOpen,
    onOpen: onEmergencyCallDialogOpen,
    onClose: onEmergencyCallDialogClose,
  } = useDisclosure()

  const {
    open: isSendWebhookModalOpen,
    onOpen: onSendWebhookModalOpen,
    onClose: onSendWebhookModalClose,
  } = useDisclosure()

  const {
    tab,
    handleAddNoteClick,
    handleDispatchClick,
    handleRadioClick,
    handleOrderRepairClick,
    handleResolveClick,
    handleSOPClick,
    closeTabs,
  } = useTabs({ mixpanelData, incidentId: incident?.id })

  const onToggleChange = (real: boolean) => {
    setIncidentReal(incident?.id, real, true, mixpanelData)
  }

  const onActionComplete = () => {
    refetchFeedItems?.()
    closeTabs()
  }

  const {
    isEmergencyCallEnabled,
    isAddNoteEnabled,
    isDispatchEnabled,
    isRadioGuardsEnabled,
    isOrderRepairEnabled,
    isResolveEnabled,
    isDownloadReportEnabled,
    isSendToOnticEnabled,
    isSendToResolverEnabled,
    isAdditionalOptionsEnabled,
  } = useAvailableIncidentActions({ incident })

  // Close the Order Repair panel if it becomes unavailable while open, e.g. the
  // owner changes under the poll
  useEffect(() => {
    if (tab === IncidentTab.OrderRepair && !isOrderRepairEnabled) {
      closeTabs()
    }
  }, [tab, isOrderRepairEnabled, closeTabs])

  const isDownloadReportBlockedByWorkflowLoading =
    isDownloadReportEnabled && hasWorkflows && isLoadingWorkflow
  const isDownloadReportActionEnabled =
    isDownloadReportEnabled && !isDownloadReportBlockedByWorkflowLoading
  // A greyed row is unexplained without this: either the workflow data hasn't
  // landed yet, or the incident isn't resolved (ENG-32094).
  const downloadReportTooltipLabel = isDownloadReportBlockedByWorkflowLoading
    ? t('incidents:download_report_workflow_loading_hint')
    : !isDownloadReportEnabled
    ? t('incidents:resolved_to_download_hint')
    : undefined
  const onticDisabledTooltipKey = getOnticDisabledTooltipKey(incident)
  const resolverDisabledTooltipKey = getResolverDisabledTooltipKey(incident)
  // Emergency call. `CallFlaggedProvider` mounts no context when the
  // emergencyCall flag is off, so useCall() returns null in those environments
  // and must never be destructured. The drawer renders on incidentDrawerRebrand
  // alone, and the two flags are set independently per environment, so an
  // environment with the rebrand on and emergencyCall off is a live
  // combination (ENG-33780).
  const call = useCall()
  const isCallOpen = call?.isOpen ?? false
  const handleEmergencyCall = () => {
    call?.openCall(
      {
        incidentId: incident?.id,
        incidentDisplayId: incident?.displayId,
        facilityName: incident?.facility?.name,
        emergencyNumber: incident?.facility?.emergencyNumber,
      },
      mixpanelData
    )
    onEmergencyCallDialogClose()
  }

  // Send to Ontic. isSendingToOntic keeps the row disabled while the mutation
  // is in flight, so reopening the menu can't fire a second send (ENG-32094).
  const { send: sendToOntic, isSendingToOntic } = useSendIncidentsToOntic(
    incident?.id,
    true
  )

  // Send to Resolver (placeholder until the integration is wired up)
  const { send: sendToResolver } = useSendIncidentToResolver(true)
  const isResolverIntegrationEnabled = useFeatureFlag(
    FeatureFlag.resolverIntegration
  )

  // Download report
  const { downloadReport } = useReportDownload({ incident, workflowReportData })

  // Feature flags for More menu items
  const isCaseManagementEnabled = useFeatureFlag(FeatureFlag.caseManagement)
  const isSendToOnticFeatureEnabled = useFeatureFlag(FeatureFlag.sendToOntic)
  const isManualWebhooksEnabled = useFeatureFlag(FeatureFlag.manualWebhooks)
  const {
    shouldEnableCaseManagement,
    shouldEnableSendToOntic,
    shouldEnableSendToResolver,
  } = usePermissions()
  const shouldShowAssociateIncident =
    isCaseManagementEnabled && shouldEnableCaseManagement
  const shouldShowSendToOntic =
    isSendToOnticFeatureEnabled && shouldEnableSendToOntic
  const shouldShowSendToResolver =
    isResolverIntegrationEnabled && shouldEnableSendToResolver
  // Grid-testing eligibility is part of the condition so the kebab doesn't
  // vanish when Grid Testing is its only item (ENG-34040).
  const shouldShowMoreMenu =
    isAdditionalOptionsEnabled ||
    shouldShowSendToOntic ||
    shouldShowSendToResolver ||
    isManualWebhooksEnabled ||
    gridTesting.isEnabled

  // Build "More" menu actions
  const moreMenuActions: ActionMenuItemType[] = []
  // Grid Testing lives in the kebab menu only — a deliberately de-emphasized
  // single entry point for both the tailgate-evaluator and ingress-detector
  // flows (ENG-34040). The mechanism is picked by incident type downstream.
  if (gridTesting.isEnabled) {
    moreMenuActions.push({
      icon: <GridTestingIcon colorScheme={colorScheme} size={16} />,
      label: t('common:grid_testing'),
      onClick: () => {
        gridTesting.trackTailgateEvent(GTZ_ENTER)
        gridTesting.open()
      },
    })
  }
  if (shouldShowAssociateIncident) {
    moreMenuActions.push({
      icon: <AssociateIncidentIcon colorScheme={colorScheme} size={16} />,
      label: t('incidents:associate_incident'),
      onClick: onAssociateCaseModalOpen,
    })
  }
  if (shouldShowSendToOntic) {
    moreMenuActions.push({
      icon: <SendIcon colorScheme={colorScheme} size={16} />,
      label: t('incidents:send_to_ontic'),
      onClick: () => {
        sendToOntic()
      },
      disabled: !isSendToOnticEnabled || isSendingToOntic,
      ...(onticDisabledTooltipKey && {
        tooltipLabel: t(onticDisabledTooltipKey),
        onTooltipShow: () => {
          trackEvent(SEND_TO_ONTIC_DISABLED_TOOLTIP_SHOWN, {
            incident_id: incident?.id,
            incident_status: incident?.status,
          })
        },
      }),
    })
  }
  if (shouldShowSendToResolver) {
    moreMenuActions.push({
      icon: <SendIcon colorScheme={colorScheme} size={16} />,
      label: t('incidents:send_to_resolver'),
      onClick: sendToResolver,
      disabled: !isSendToResolverEnabled,
      ...(resolverDisabledTooltipKey && {
        tooltipLabel: t(resolverDisabledTooltipKey),
      }),
    })
  }
  if (isManualWebhooksEnabled) {
    moreMenuActions.push({
      icon: <SendIcon colorScheme={colorScheme} size={16} />,
      label: t('incidents:send_webhook'),
      onClick: onSendWebhookModalOpen,
      disabled: !incident?.id,
    })
  }
  // Only when the kebab would have opened for its own reasons — the menu can
  // now also open for Grid Testing alone (ENG-34040), and a disabled Download
  // Report shouldn't appear in that case, as it wouldn't have pre-change.
  if (isAdditionalOptionsEnabled) {
    moreMenuActions.push({
      icon: <DownloadIcon colorScheme={colorScheme} size={16} />,
      label: t('incidents:download_report'),
      onClick: downloadReport,
      disabled: !isDownloadReportActionEnabled,
      ...(downloadReportTooltipLabel && {
        tooltipLabel: downloadReportTooltipLabel,
      }),
    })
  }

  // Build guards menu actions
  const guardsMenuActions: ActionMenuItemType[] = []
  guardsMenuActions.push({
    label: t('incidents:dispatch_guards'),
    onClick: handleDispatchClick,
    disabled: !isDispatchEnabled,
  })
  guardsMenuActions.push({
    label: t('incidents:radio_guards'),
    onClick: handleRadioClick,
    disabled: !isRadioGuardsEnabled,
  })

  return (
    <Box pb='0'>
      {/* Location breadcrumb, with the map panel trigger leading the trail —
          it belongs to the location context, not the incident action row.
          The trigger leads at 24px so its left edge lines up with the header
          and action-bar content (ENG-33658); without it the breadcrumb's own
          16px padding keeps the original layout. */}
      {!isLoading && incident && (
        <Box
          alignItems='center'
          display='flex'
          pl={shouldShowMapPanelButton ? '24px' : '0'}
        >
          {shouldShowMapPanelButton && (
            <UtilityButton
              colorScheme={colorScheme}
              disabled={isMapPanelTooNarrow}
              icon={<MapIcon size={14} />}
              isSelected={isMapPanelDrawerOpen}
              label={t('incidents:drawer_map_panel')}
              onClick={() => {
                if (isMapPanelDrawerOpen) {
                  trackEvent(MAP_PANEL_DRAWER_CLOSE, {
                    ...mixpanelData,
                    closed_via: 'toggle_button',
                  })
                } else {
                  trackEvent(MAP_PANEL_DRAWER_OPEN, mixpanelData)
                }
                dispatch(closeSOPIncidentDrawer())
                dispatch(closeTraceLogDrawer())
                dispatch(closeWorkflowDrawer())
                dispatch(toggleIncidentMapPanelDrawer())
              }}
              stylesOverride={MAP_PANEL_BUTTON_SIZE}
              tooltipLabel={t('incidents:drawer_map_panel')}
            />
          )}
          <LocationBreadcrumb
            colorScheme={colorScheme}
            copiedLabel={t('common:copied')}
            copyLabel={t('common:copy_to_clipboard')}
            items={[
              ...(incident.devices?.[0]?.name
                ? [
                    {
                      label: incident.devices[0].name,
                      // Truncated labels need the full name on hover, and the
                      // ARCHIVED suffix has to survive here too — legacy puts a
                      // tooltip on every crumb, not just the facility (ENG-32089).
                      tooltipLabel: getArchivedLabel(
                        incident.devices[0].name,
                        isFacilityArchived
                      ),
                      // Omitted for types DeviceDrawer can't render (Alarm,
                      // Checkpoint) so the crumb stays copy-only rather than
                      // dead-clicking into a stuck-open drawer.
                      onClick: isDeviceDrawerSupported(
                        incident.devices[0]?.type
                      )
                        ? () => {
                            const device = incident.devices?.[0]
                            dispatch(
                              setDevice({ id: device?.id, type: device?.type })
                            )
                            dispatch(openDeviceDrawer())
                          }
                        : undefined,
                    },
                  ]
                : []),
              ...(incident.floor?.name
                ? [
                    {
                      label: `Floor ${incident.floor.name}`,
                      tooltipLabel: getArchivedLabel(
                        `Floor ${incident.floor.name}`,
                        isFacilityArchived
                      ),
                      href: getFloorPlanHref(incident.floor.id),
                      onClick: () => {
                        onClose()
                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            goToFloorPlan(incident.floor.id)
                          }, 0)
                        })
                      },
                    },
                  ]
                : []),
              ...(incident.facility?.shortName
                ? [
                    {
                      label: incident.facility.shortName,
                      // The crumb shows only the short code, so the tooltip
                      // spells out the full name and repeats the code it
                      // stands for (ENG-32089).
                      tooltipLabel: getArchivedLabelWithSN(incident.facility),
                      // Lands on the incident's floor when there is one, else
                      // the facility's default floor. No resolvable floor means
                      // no link, per ENG-32089.
                      ...(incident.facility.type === FacilityType.Normal &&
                      facilityFloorId
                        ? {
                            href: getFloorPlanHref(facilityFloorId),
                            onClick: () => {
                              onClose()
                              requestAnimationFrame(() => {
                                setTimeout(() => {
                                  goToFloorPlan(facilityFloorId)
                                }, 0)
                              })
                            },
                          }
                        : {}),
                    },
                  ]
                : []),
            ]}
            onCopyError={handleBreadcrumbCopyError}
          />
        </Box>
      )}

      {/* Action bar */}
      <Box
        alignItems='center'
        borderBottom='1px solid'
        borderColor={`${prefix}border.subtle`}
        display='flex'
        justifyContent='space-between'
        pb='12px'
        pt='12px'
        px='24px'
        width='100%'
      >
        {/* Left: Workflow button */}
        {shouldShowWorkflowButton && (
          <WorkflowButton
            colorScheme={colorScheme}
            disabled={isWorkflowDisabled}
            isActive={isWorkflowDrawerOpen}
            onClick={() => {
              dispatch(closeSOPIncidentDrawer())
              dispatch(closeTraceLogDrawer())
              dispatch(closeIncidentMapPanelDrawer())
              dispatch(toggleWorkflowDrawer())
            }}
            tooltipLabel={isWorkflowDisabled ? workflowTooltipLabel : undefined}
          />
        )}

        {/* Center: Resolve toggle (resolved only) */}
        {incident?.status === IncidentStatus.Resolved && (
          <ResolveToggle
            colorScheme={colorScheme}
            isLoading={isUpdatingIncidentReal}
            isReal={incident?.real}
            onChange={onToggleChange}
          />
        )}

        {/* Right: Action buttons */}
        <Box alignItems='center' display='flex' gap='8px'>
          {/* Add Note */}
          {isAddNoteEnabled && (
            <UtilityButton
              colorScheme={colorScheme}
              icon={<NoteIcon size={18} />}
              isSelected={tab === IncidentTab.AddNote}
              label={t('common:add_note')}
              onClick={handleAddNoteClick}
              stylesOverride={ACTION_BUTTON_SIZE}
              tooltipLabel={t('common:add_note')}
            />
          )}

          {/* Standard Procedures */}
          <UtilityButton
            colorScheme={colorScheme}
            disabled={isSOPDisabled}
            icon={<SOPIcon size={18} />}
            isSelected={sopDrawerState?.isOpen === true}
            label={t('incidents:drawer_sop_standard_procedures')}
            onClick={() => {
              handleSOPClick()
              dispatch(closeTraceLogDrawer())
              dispatch(closeWorkflowDrawer())
              dispatch(closeIncidentMapPanelDrawer())
              dispatch(toggleSOPIncidentDrawer())
            }}
            stylesOverride={ACTION_BUTTON_SIZE}
            tooltipLabel={t('incidents:drawer_sop_standard_procedures')}
          />

          {/* Assign Guards (dropdown) */}
          {(isDispatchEnabled || isRadioGuardsEnabled) && (
            <ActionMenu
              colorScheme={colorScheme}
              items={guardsMenuActions}
              trigger={({ onClick, isOpen: menuOpen }) => (
                <UtilityButton
                  colorScheme={colorScheme}
                  icon={<GuardsIcon size={18} />}
                  isSelected={
                    menuOpen ||
                    tab === IncidentTab.DispatchGuards ||
                    tab === IncidentTab.RadioGuards
                  }
                  label={t('incidents:assign_guards')}
                  onClick={onClick}
                  stylesOverride={ACTION_BUTTON_SIZE}
                  tooltipLabel={t('incidents:assign_guards')}
                />
              )}
            />
          )}

          {/* Order Repair (technician contact) */}
          {isOrderRepairEnabled && (
            <UtilityButton
              colorScheme={colorScheme}
              icon={<ToolIcon size={18} />}
              isSelected={tab === IncidentTab.OrderRepair}
              label={t('incidents:order_repair')}
              onClick={handleOrderRepairClick}
              stylesOverride={ACTION_BUTTON_SIZE}
              tooltipLabel={t('incidents:order_repair')}
            />
          )}

          {/* Emergency Call */}
          {isEmergencyCallEnabled && (
            <UtilityButton
              colorScheme={colorScheme}
              disabled={isCallOpen}
              icon={<EmergencyCallFilledIcon size={18} />}
              iconColor='var(--hw-dark-colors-interactive-danger, #E53E3E)'
              isSelected={false}
              label={t('emergency_call:emergency_call')}
              onClick={onEmergencyCallDialogOpen}
              stylesOverride={ACTION_BUTTON_SIZE}
              tooltipLabel={t('emergency_call:emergency_call')}
            />
          )}

          {/* Resolve */}
          {isResolveEnabled && (
            <UtilityButton
              colorScheme={colorScheme}
              disabled={isResolveBlockedByWorkflow}
              icon={<ResolveIcon size={18} />}
              isSelected={tab === IncidentTab.Resolve}
              label={t('incidents:resolve')}
              onClick={handleResolveClick}
              stylesOverride={ACTION_BUTTON_SIZE}
              tooltipLabel={
                isResolveBlockedByWorkflow
                  ? t('incidents:resolve_blocked_by_workflow')
                  : t('incidents:resolve')
              }
            />
          )}

          {/* AIO Eval */}
          {isInternalImplementer && (
            <UtilityButton
              colorScheme={colorScheme}
              data-testid='aio-eval-results-action-button'
              disabled={!incident?.id}
              icon={<AiOperatorActionIcon />}
              isSelected={false}
              label={t('incidents:aio_eval_results')}
              onClick={aioEval.handleOpen}
              stylesOverride={ACTION_BUTTON_SIZE}
              tooltipLabel={t('incidents:aio_eval_results')}
            />
          )}

          {/* More menu */}
          {shouldShowMoreMenu && (
            <ActionMenu
              anchorRight
              colorScheme={colorScheme}
              items={moreMenuActions}
              trigger={({ onClick, isOpen: menuOpen }) => (
                <UtilityButton
                  colorScheme={colorScheme}
                  icon={
                    <KebabHorizontalIcon colorScheme={colorScheme} size={16} />
                  }
                  isSelected={menuOpen}
                  label={t('common:more')}
                  onClick={onClick}
                  stylesOverride={ACTION_BUTTON_SIZE}
                  tooltipLabel={t('common:more')}
                />
              )}
            />
          )}
        </Box>
      </Box>

      {/* Animated panels */}
      <Box>
        {tab === IncidentTab.AddNote && (
          <AnimatedPanel>
            <AddNote
              colorScheme={colorScheme}
              incidentId={incident?.id}
              mixpanelData={mixpanelData}
              onActionComplete={closeTabs}
            />
          </AnimatedPanel>
        )}
        {tab === IncidentTab.DispatchGuards && (
          <AnimatedPanel>
            <DispatchWhiteListAlert incident={incident} />
            <GuardPanel
              colorScheme={colorScheme}
              incident={incident}
              mixpanelData={mixpanelData}
              onActionComplete={onActionComplete}
            />
          </AnimatedPanel>
        )}
        {tab === IncidentTab.RadioGuards && (
          <AnimatedPanel>
            <GuardPanel
              colorScheme={colorScheme}
              incident={incident}
              mixpanelData={mixpanelData}
              mode='radio'
              onActionComplete={onActionComplete}
            />
          </AnimatedPanel>
        )}
        {tab === IncidentTab.OrderRepair && isOrderRepairEnabled && (
          <AnimatedPanel>
            {/* Key by incident id so the draft (technician + message) resets
                when navigating to another incident without unmounting the
                action bar — e.g. a cached next/prev incident (ENG-27498). */}
            <OrderRepair
              colorScheme={colorScheme}
              deviceType={gridTesting.device?.type}
              facilityId={incident?.facility?.id}
              incidentId={incident?.id}
              incidentStatus={incident?.status}
              isEnabled={isOrderRepairEnabled}
              key={incident?.id}
              mixpanelData={mixpanelData}
              onActionComplete={onActionComplete}
            />
          </AnimatedPanel>
        )}
        {tab === IncidentTab.Resolve && (
          <AnimatedPanel>
            <Resolve
              colorScheme={colorScheme}
              incident={incident}
              mixpanelData={mixpanelData}
              onActionComplete={closeTabs}
            />
          </AnimatedPanel>
        )}
      </Box>

      {/* Modals */}
      <AssociateCaseModal
        incidentId={incident?.id}
        isOpen={isAssociateCaseModalOpen}
        onClose={onAssociateCaseModalClose}
      />
      {isManualWebhooksEnabled && (
        <SendWebhookModal
          incidentId={incident?.id}
          isOpen={isSendWebhookModalOpen}
          onClose={onSendWebhookModalClose}
        />
      )}
      {isInternalImplementer && (
        <AioEvalResultsModals
          error={aioEval.error}
          evaluations={aioEval.evaluations}
          isLoading={aioEval.isLoading}
          isOpen={aioEval.isResultsOpen}
          onClose={aioEval.onResultsClose}
        />
      )}
      {isEmergencyCallDialogOpen && (
        <ConfirmEmergencyCallDialog
          emergencyNumber={incident?.facility?.emergencyNumber}
          handleOnClick={handleEmergencyCall}
          isOpen={isEmergencyCallDialogOpen}
          onClose={onEmergencyCallDialogClose}
        />
      )}
      {gridTesting.isEnabled &&
        gridTesting.gridCameraId &&
        gridTesting.device?.id && (
          <TailgateGridProvider
            associateDeviceValues={{
              cameraId: gridTesting.gridCameraId,
              cameraName: gridTesting.gridCameraName,
              doorId: gridTesting.device.id,
              doorName: gridTesting.device.name ?? '',
              deviceType: DeviceType.Door,
            }}
            isGridConfigured={gridTesting.isGridConfigured}
            isTailgateGridOpen={gridTesting.isOpen}
            onTailgateGridClose={gridTesting.close}
            shouldRenderDummyPlayer={gridTesting.isOpen}
            trackTailgateEvent={gridTesting.trackTailgateEvent}
          >
            <StandaloneGridTestingModal
              colorScheme={colorScheme}
              isOpen={gridTesting.isOpen}
              onClose={gridTesting.close}
              presetIncident={gridTesting.presetIncident}
              zIndex={1500}
            />
          </TailgateGridProvider>
        )}
    </Box>
  )
}
